import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, ReplaySubject } from 'rxjs';
import { tap, shareReplay, catchError, filter, take } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environments';

export interface ProfileData {
  _id: string;
  email: string;
  name: string;
  rol: string;
  stations: any[];
  idStation?: string;
  [key: string]: any;
}

@Injectable({
  providedIn: 'root'
})
export class ProfileCacheService {
  private endpoint1 = environment.apiUrl + '/v1';
  private profileCache$: Observable<ProfileData> | null = null;
  private profileSubject = new BehaviorSubject<ProfileData | null>(null);
  private profileReadySubject = new ReplaySubject<boolean>(1);

  /**
   * Observable que emite los datos del perfil cuando cambian.
   * Emite null cuando no hay perfil cargado.
   */
  public profile$ = this.profileSubject.asObservable();

  /**
   * Observable que emite true cuando el perfil está completamente cargado.
   * Útil para esperar a que el perfil esté listo antes de ejecutar otras acciones.
   */
  public profileReady$ = this.profileReadySubject.asObservable();

  constructor(private http: HttpClient) {
    // Marcar como no listo inicialmente
    this.profileReadySubject.next(false);
  }

  /**
   * Obtiene el perfil del usuario con caché automático.
   * Solo hace una llamada al servidor la primera vez, las llamadas subsiguientes
   * devuelven el valor cacheado.
   *
   * @param dios - Indica si el usuario tiene rol "Dios"
   * @param group - ID del grupo (opcional, requerido si dios es false)
   * @returns Observable con los datos del perfil
   */
  getProfile(dios: boolean, group?: string): Observable<ProfileData> {
    // Si ya existe un observable en cache, retornarlo
    if (this.profileCache$) {
      return this.profileCache$;
    }

    // Construir la URL según los parámetros
    const url = dios
      ? `${this.endpoint1}/crm/users/profile`
      : `${this.endpoint1}/crm/users/profile?group=${group}`;

    // Crear un nuevo observable con shareReplay para cachear el resultado
    this.profileCache$ = this.http.get<ProfileData>(url).pipe(
      tap(profile => {
        // Actualizar el BehaviorSubject con los datos del perfil
        this.profileSubject.next(profile);

        // Marcar el perfil como listo
        this.profileReadySubject.next(true);

        // Guardar datos importantes en localStorage para persistencia
        if (profile._id) {
          localStorage.setItem('id', profile._id);
        }
        if (profile.rol) {
          localStorage.setItem('rol', profile.rol);
        }
        if (profile.stations && profile.stations.length > 0) {
          localStorage.setItem('stations', JSON.stringify(profile.stations));
        }
      }),
      // shareReplay(1) mantiene el último valor emitido en cache
      // y lo comparte con todos los suscriptores
      shareReplay({ bufferSize: 1, refCount: false }),
      catchError(error => {
        // En caso de error, limpiar el cache para permitir reintentos
        this.clearCache();
        throw error;
      })
    );

    return this.profileCache$;
  }

  /**
   * Limpia el cache del perfil.
   * Útil cuando el usuario cierra sesión o cuando se necesita forzar
   * una nueva carga de datos del servidor.
   */
  clearCache(): void {
    this.profileCache$ = null;
    this.profileSubject.next(null);
    this.profileReadySubject.next(false);
  }

  /**
   * Obtiene el valor actual del perfil sin hacer petición al servidor.
   * Útil para acceso síncrono a datos ya cargados.
   *
   * @returns Los datos del perfil actual o null si no están cargados
   */
  getCurrentProfile(): ProfileData | null {
    return this.profileSubject.value;
  }

  /**
   * Actualiza parcialmente el perfil en cache sin hacer petición al servidor.
   * Útil para actualizaciones locales que no requieren sincronización inmediata.
   *
   * @param partialProfile - Objeto con las propiedades a actualizar
   */
  updateProfileLocally(partialProfile: Partial<ProfileData>): void {
    const current = this.profileSubject.value;
    if (current) {
      this.profileSubject.next({ ...current, ...partialProfile });
    }
  }

  /**
   * Verifica si el perfil está cargado en memoria
   *
   * @returns true si el perfil está cargado, false en caso contrario
   */
  isProfileLoaded(): boolean {
    return this.profileSubject.value !== null;
  }

  /**
   * Retorna un Observable que emite solo cuando el perfil está completamente cargado.
   * Útil para esperar a que el perfil esté listo antes de ejecutar otras operaciones.
   *
   * @returns Observable<ProfileData> que emite el perfil cuando está listo
   *
   * @example
   * // Esperar a que el perfil esté listo antes de cargar datos
   * this.profileCache.waitForProfile().subscribe(profile => {
   *   this.loadUserData(profile._id);
   * });
   */
  waitForProfile(): Observable<ProfileData> {
    return this.profile$.pipe(
      filter((profile): profile is ProfileData => profile !== null),
      take(1)
    );
  }

  /**
   * Ejecuta una función callback solo cuando el perfil está completamente cargado.
   * Si el perfil ya está cargado, ejecuta el callback inmediatamente.
   *
   * @param callback - Función a ejecutar con los datos del perfil
   *
   * @example
   * this.profileCache.whenReady(profile => {
   *   console.log('Perfil listo:', profile.name);
   *   this.loadDashboard(profile._id);
   * });
   */
  whenReady(callback: (profile: ProfileData) => void): void {
    const currentProfile = this.getCurrentProfile();
    if (currentProfile) {
      // Si ya está cargado, ejecutar inmediatamente
      callback(currentProfile);
    } else {
      // Si no, esperar a que se cargue
      this.waitForProfile().subscribe(callback);
    }
  }
}
